import { Component, Input, OnInit } from '@angular/core';
import {
  AdvancedCredentials, BasicCredentials,
  CredentialGroup,
  Credentials,
  CredentialsAssignment,
  CredentialsAssignments,
  CredentialsType
} from '@app/shared/models/credentials';
import { ComponentServiceBundle } from '@app/core/services/base/component-service-bundle.service';
import { BaseUpdateComponent } from '@app/shared/components/base/base-update.component';
import { CredentialsService, CredentialsUriBuilderParams } from '@app/core/services/credentials.service';
import { BPCConfig } from '@app/shared/components/base/base-change.component';
import { BaseList } from '@app/shared/models/list';
import { EmailProvider } from '@app/shared/models/email-provider';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { DRResponse, ResponseStatus } from '@app/shared/models/response';
import { TranslationEntry } from '@app/shared/models/translatable';
import { SearchFilter } from '@app/shared/models/search-filter';

@Component({
  selector: 'smp-credentials',
  templateUrl: './credentials.component.html',
  styleUrls: ['./credentials.component.scss']
})
export class CredentialsComponent extends BaseUpdateComponent<Credentials, CredentialsUriBuilderParams>
                                  implements OnInit {

  @Input()
  public credentialsType: CredentialsType;

  @Input()
  public emailProviders?: BaseList<EmailProvider>;

  protected myConfig: BPCConfig = { fetchAtInit: true }; // override in sub class if object of T needs to be fetched during component init.
  protected paramsIdNames = [];

  public selectedAdvancedCredentials: AdvancedCredentials;

  private _selectedCredentialsAssignment: TranslationEntry;

  get selectedCredentialsAssignment() {
    return this._selectedCredentialsAssignment;
  }

  set selectedCredentialsAssignment(assignment: TranslationEntry) {
    this._selectedCredentialsAssignment = assignment;

    this.selectedAdvancedCredentials = null;
    this.credentials.advanced = this._selectedCredentialsAssignment !== CredentialsAssignments.BASIC;
    this.credentials.assignment = this.credentials.advanced ? assignment.key as CredentialsAssignment : null;
    this.save(true);
  }

  get credentials(): Credentials {
    return this.updated;
  }

  set credentials(credentials: Credentials) {
    this.updated = credentials;
  }

  get advancedCredentialGroup(): CredentialGroup {
    if (this.credentials.advanced) {
      return this.credentials.assignment === CredentialsAssignment.OFFICES ? this.credentials.groups && this.credentials.groups.offices :
             this.credentials.assignment === CredentialsAssignment.USERS ? this.credentials.groups && this.credentials.groups.users :
             null;
    } else {
      return null;
    }
  }

  get originalAdvancedCredentialGroup(): CredentialGroup {
    if (this.original.advanced) {
      return this.original.assignment === CredentialsAssignment.OFFICES ? this.original.groups && this.original.groups.offices :
             this.original.assignment === CredentialsAssignment.USERS ? this.original.groups && this.original.groups.users :
             null;
    } else {
      return null;
    }
  }

  get CredentialType() {
    return CredentialsType;
  }

  get CredentialsAssignment() {
    return CredentialsAssignment;
  }

  get CredentialsAssignments() {
    return CredentialsAssignments;
  }

  public filter: SearchFilter<AdvancedCredentials>;

  constructor(componentServiceBundle: ComponentServiceBundle, protected credentialsService: CredentialsService) {
    super(componentServiceBundle, credentialsService);
  }

  public ngOnInit() {
    super.ngOnInit();
    this.filter = new SearchFilter(false, true);
  }

  protected whenOriginalFetched(): void {
    super.whenOriginalFetched();
    this.initAssignment();
    this.filter.array = this.advancedCredentialGroup ? this.advancedCredentialGroup.credentials : [];
  }

  private initAssignment(): void {
    if (this.credentials.advanced) {
      this._selectedCredentialsAssignment = CredentialsAssignments.getByKey(this.credentials.assignment);
    } else {
      this._selectedCredentialsAssignment = CredentialsAssignments.BASIC;
    }
  }

  public cancel(): void {
    this.credentials.basic = Credentials.generateCredentialsBasedOnType(this.original.basic, this.original.credentialsType) as BasicCredentials;
  }

  public save(typeChange?: boolean): void {
    this.update(null, () => {
      this.getOriginal();

      if (!typeChange) {
        this.savedCredentialsSuccessMessage();
      }
    }, err => {
      this.handleError(err);
      this.savedCredentialsErrorMessage();
    });
  }

  public saveAdvancedCredentials(credentials: AdvancedCredentials): void {
    this.credentialsService.updateAdvancedCredentials(this.generateUriBuilderParams(), credentials)
                           .subscribe((result) => {
        this.advancedCredentialGroup.replaceCredentialsValues(result);
        this.originalAdvancedCredentialGroup.replaceCredentialsValues(result);
        this.savedCredentialsSuccessMessage();
    }, err => {
       this.handleError(err);
       this.savedCredentialsErrorMessage();
    });
  }

  public cancelAdvancedCredentials(credentials: AdvancedCredentials): void {
    const original = Credentials.generateCredentialsBasedOnType(this.originalAdvancedCredentialGroup.getCredentialsById(credentials.id), this.credentials.credentialsType, this.credentials.assignment) as AdvancedCredentials;
    this.advancedCredentialGroup.setCredentialsById(original);
    this.filter.filter();
  }

  public handleTestEmailResponse(response: DRResponse<any>): void {
    if (response.status === ResponseStatus.SUCCESS) {
      this.showSimpleConfirmSnackbar(marker('third_party_credentials_management.message.test_email_success'));
    } else if (response.status === ResponseStatus.FAIL) {
      this.showSimpleInfoDialog(marker('third_party_credentials_management.message.test_email_failed.title'),
                          [marker('third_party_credentials_management.message.test_email_failed.text'), response.message],
                         '600px');
    }
  }

  public selectAdvancedCredentials(cred: AdvancedCredentials): void {
    this.selectedAdvancedCredentials = this.selectedAdvancedCredentials ? (this.selectedAdvancedCredentials.id === cred.id ? null : cred) : cred;
  }

  protected generateUriBuilderParams(): CredentialsUriBuilderParams {
    return new CredentialsUriBuilderParams(this.credentialsType);
  }

  protected savedCredentialsSuccessMessage(): void {
    this.showSimpleConfirmSnackbar(marker('third_party_credentials_management.message.credentials_saved'));
  }

  protected savedCredentialsErrorMessage(): void {
    this.showSimpleErrorSnackbar(marker('third_party_credentials_management.message.credentials_save_failed'));
  }

  // Override of the main method
  protected reInitOriginalAndUpdated(result: Credentials): void {
    if (!this.original && !this.updated) {
      const constructorX = this.drHttpService.getConstructor(this.drHttpService.constructorT, result);
      this.original = new constructorX(result);
      this.updated = new constructorX(this.original);
    } else {
      this.original.mergeWith(result);
      this.updated.mergeWith(result);
    }
  }
}
